import { ReactNode, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { PublicClientApplication } from '@azure/msal-browser';
import { keepPreviousData, MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { message, Space, Spin } from 'antd';
import { AxiosError, AxiosResponse } from 'axios';

import { Role } from '@app/domain-interfaces';
import { Auth, Axios, PageLayout } from '@app/web-components';
import { Snapshot } from '@app/web-t3-pages';

import { getConfig } from './config';

const config = getConfig();

export const msalInstance = new PublicClientApplication({
  auth: config.auth,
  cache: {
    cacheLocation: 'localStorage', // where cache will be stored
    storeAuthStateInCookie: false, // set to "true" if you are having issues on IE11 or Edge
  },
});

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const LinkMenuItem = (to: string, label: ReactNode, roles?: Role[], target?: string): PageLayout.NavMenuItemType => ({
  key: to,
  label: (
    <Link to={to} target={target}>
      {label}
    </Link>
  ),
  roles,
});

const navs: PageLayout.NavMenuItemType[] = [];

const App = () => {
  const config = getConfig();
  const { user, error: authError } = Auth.useUserAuth();
  const [messageApi, messageContextHolder] = message.useMessage();

  const queryClient = useMemo(
    () =>
      new QueryClient({
        queryCache: new QueryCache({
          onError: (error, query) => {
            const axiosError = error as AxiosError;
            const errorResponseData = axiosError.response as AxiosResponse<{ message: string }>;
            const errorMessage = errorResponseData?.data?.message;
            messageApi.open({
              type: 'error',
              content:
                errorMessage ??
                (query.meta?.errorMessage
                  ? (query.meta.errorMessage as string)
                  : `Something went wrong: ${axiosError.message}`),
            });
          },
        }),
        mutationCache: new MutationCache({
          onError: (error, _query, _variables, mutation) => {
            const axiosError = error as AxiosError;
            const errorResponseData = axiosError.response as AxiosResponse<{ message: string[] | string }>;
            const errorMessage = errorResponseData?.data?.message;
            messageApi.open({
              type: 'error',
              content: errorMessage ? (
                Array.isArray(errorMessage) ? (
                  <Space direction="vertical">
                    {errorMessage.map((message) => (
                      <div>{message}</div>
                    ))}
                  </Space>
                ) : (
                  errorMessage
                )
              ) : mutation.meta?.errorMessage ? (
                (mutation.meta.errorMessage as string)
              ) : (
                `Something went wrong: ${axiosError.message}`
              ),
            });
          },
        }),
        defaultOptions: {
          queries: {
            refetchOnWindowFocus: false,
            retry: process.env.NODE_ENV === 'production' ? 2 : 0,
            placeholderData: keepPreviousData,
            staleTime: 30000,
          },
        },
      }),
    [messageApi],
  );

  return (
    <QueryClientProvider client={queryClient}>
      <Axios.AxiosApiProvider baseUrl={config.apiMain} scopes={config.sso.scopes}>
        {user?.roles ? (
          authError ? (
            <Auth.ErrorPage errorMsg={authError ?? 'Oops! looks like something went wrong.'} />
          ) : (
            <Snapshot.SnapshotProvider>
              {messageContextHolder}
              <PageLayout.ExternalShell
                logoUrl="../assets/images/intel-logo.svg"
                appName="T3"
                items={navs}
                environment={config.environment}
                showEnvironment={config.showEnvironment}
              />
            </Snapshot.SnapshotProvider>
          )
        ) : (
          <Spin size="large" style={{ minHeight: '100vh' }}>
            <div style={{ height: '100vh' }} />
          </Spin>
        )}
      </Axios.AxiosApiProvider>
    </QueryClientProvider>
  );
};

export default App;
